import {
  Building2,
  Calendar,
  SlidersHorizontal,
  Users,
} from 'lucide-react'
import { useState } from 'react'

import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Label } from '@/components/ui/label'
import { ScrollArea } from '@/components/ui/scroll-area'
import { Separator } from '@/components/ui/separator'
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetFooter,
  SheetHeader,
  SheetTitle,
  SheetTrigger,
} from '@/components/ui/sheet'
import { useAsyncProviders, useAsyncManagers, useAsyncUsers } from '../hooks/useAsyncSearch'
import { useShipmentNotificationFilters } from './hooks/useShipmentNotificationFilters'
import AsyncSelect from '../components/AsyncSelect'
import { useDebounce } from '@/hooks/useDebounce'

export const AdvancedFilters = () => {
  const [isFilterDrawerOpen, setIsFilterDrawerOpen] = useState(false)

  const { searchProviders } = useAsyncProviders()
  const { searchManagers } = useAsyncManagers()
  const { searchUsers } = useAsyncUsers()

  const {
    filters,
    updateProviderCompanyId,
    updateManagerCompanyId,
    updateCreatedById,
    clearAllFilters,
  } = useShipmentNotificationFilters()

  // Local state for staged filter values
  const [localProvider, setLocalProvider] = useState(filters.providerCompanyId?.[0] ? parseInt(filters.providerCompanyId[0]) : 0)
  const [localManager, setLocalManager] = useState(filters.managerCompanyId?.[0] ? parseInt(filters.managerCompanyId[0]) : 0)
  const [localCreatedBy, setLocalCreatedBy] = useState(filters.createdById?.[0] ? parseInt(filters.createdById[0]) : 0)

  // Estados locales para búsqueda y debounce
  const [providerSearch, setProviderSearch] = useState('')
  const [managerSearch, setManagerSearch] = useState('')
  const [userSearch, setUserSearch] = useState('')
  const debouncedProviderSearch = useDebounce(providerSearch, 500)
  const debouncedManagerSearch = useDebounce(managerSearch, 500)
  const debouncedUserSearch = useDebounce(userSearch, 500)

  // Sync local state when opening drawer
  const handleOpenChange = (open: boolean) => {
    setIsFilterDrawerOpen(open)
    if (open) {
      setLocalProvider(filters.providerCompanyId?.[0] ? parseInt(filters.providerCompanyId[0]) : 0)
      setLocalManager(filters.managerCompanyId?.[0] ? parseInt(filters.managerCompanyId[0]) : 0)
      setLocalCreatedBy(filters.createdById?.[0] ? parseInt(filters.createdById[0]) : 0)
    }
  }

  const handleApplyFilters = () => {
    updateProviderCompanyId(localProvider ? [localProvider.toString()] : ['all'])
    updateManagerCompanyId(localManager ? [localManager.toString()] : ['all'])
    updateCreatedById(localCreatedBy ? [localCreatedBy.toString()] : ['all'])
    setIsFilterDrawerOpen(false)
  }

  const handleClearAllFilters = () => {
    clearAllFilters()
    setIsFilterDrawerOpen(false)
  }

  // Count active advanced filters
  const getActiveAdvancedFiltersCount = () => {
    let count = 0
    if (filters.providerCompanyId && filters.providerCompanyId.length > 0 && !filters.providerCompanyId.includes('all')) count++
    if (filters.managerCompanyId && filters.managerCompanyId.length > 0 && !filters.managerCompanyId.includes('all')) count++
    if (filters.createdById && filters.createdById.length > 0 && !filters.createdById.includes('all')) count++
    return count
  }

  return (
    <Sheet open={isFilterDrawerOpen} onOpenChange={handleOpenChange}>
      <SheetTrigger asChild>
        <Button
          variant="outline"
          className="gap-1.5"
          aria-label="Filtros avanzados"
        >
          <SlidersHorizontal className="h-4 w-4" />
          <span className="hidden sm:inline">Filtros avanzados</span>
          {getActiveAdvancedFiltersCount() > 0 && (
            <Badge variant="secondary" className="ml-1 rounded-full px-1.5">
              {getActiveAdvancedFiltersCount()}
            </Badge>
          )}
        </Button>
      </SheetTrigger>
      <SheetContent className="sm:max-w-lg">
        <SheetHeader>
          <SheetTitle>Filtros avanzados</SheetTitle>
          <SheetDescription>
            Configura filtros específicos para encontrar exactamente lo que
            buscas.
          </SheetDescription>
        </SheetHeader>

        <ScrollArea className="h-[calc(100vh-180px)] pr-4">
          <div className="space-y-6 py-6">
            {/* Proveedor */}
            <div className="space-y-2">
              <div className="flex items-center space-x-2">
                <Building2 className="h-4 w-4 text-gray-500" />
                <h3 className="text-sm font-medium">Proveedor</h3>
              </div>
              <div className="space-y-2">
                <Label htmlFor="provider">Seleccionar proveedor</Label>
                <AsyncSelect
                  value={localProvider}
                  onChange={setLocalProvider}
                  onSearch={searchProviders}
                  searchQuery={providerSearch}
                  setSearchQuery={setProviderSearch}
                  debouncedSearchQuery={debouncedProviderSearch}
                  placeholder="Seleccione un proveedor"
                  label=""
                  error=""
                  required={false}
                  disabled={false}
                />
              </div>
            </div>

            <Separator />

            {/* Operador Logístico */}
            <div className="space-y-2">
              <div className="flex items-center space-x-2">
                <Building2 className="h-4 w-4 text-gray-500" />
                <h3 className="text-sm font-medium">Operador Logístico</h3>
              </div>
              <div className="space-y-2">
                <Label htmlFor="manager">Seleccionar operador</Label>
                <AsyncSelect
                  value={localManager}
                  onChange={setLocalManager}
                  onSearch={searchManagers}
                  searchQuery={managerSearch}
                  setSearchQuery={setManagerSearch}
                  debouncedSearchQuery={debouncedManagerSearch}
                  placeholder="Seleccione un operador logístico"
                  label=""
                  error=""
                  required={false}
                  disabled={false}
                />
              </div>
            </div>

            <Separator />

            {/* Creado por */}
            <div className="space-y-2">
              <div className="flex items-center space-x-2">
                <Users className="h-4 w-4 text-gray-500" />
                <h3 className="text-sm font-medium">Creado por</h3>
              </div>
              <div className="space-y-2">
                <Label htmlFor="createdBy">Seleccionar usuario</Label>
                <AsyncSelect
                  value={localCreatedBy}
                  onChange={setLocalCreatedBy}
                  onSearch={searchUsers}
                  searchQuery={userSearch}
                  setSearchQuery={setUserSearch}
                  debouncedSearchQuery={debouncedUserSearch}
                  placeholder="Seleccione un usuario"
                  label=""
                  error=""
                  required={false}
                  disabled={false}
                />
              </div>
            </div>
          </div>
        </ScrollArea>

        <SheetFooter className="flex-row justify-between gap-2 sm:justify-between">
          <Button
            variant="outline"
            onClick={handleClearAllFilters}
            className="flex-1"
          >
            Limpiar
          </Button>
          <Button onClick={handleApplyFilters} className="flex-1">
            Aplicar
          </Button>
        </SheetFooter>
      </SheetContent>
    </Sheet>
  )
} 